import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  BookOpen,
  Sparkles,
  CheckCircle2,
  ExternalLink,
  Lock,
  Copy,
  ArrowRight,
  ArrowLeft,
  Check,
  ShieldCheck,
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { getLeadSource } from "../lib/leadTracker";

const MAGAZINE_URL = "https://e.issuu.com/embed.html?d=producers_today_-_tom_hegna_&u=mot-suc";
const GHL_WEBHOOK_URL = process.env.REACT_APP_GHL_BOOKING_WEBHOOK_URL;
const BRAND_NAME = process.env.REACT_APP_BRAND_NAME || "producers.today";

export default function MagazinePage() {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submittedName, setSubmittedName] = useState("");
  const [copied, setCopied] = useState(false);

  // Check if visitor already unlocked magazine access
  useEffect(() => {
    try {
      const unlocked = localStorage.getItem("producers_today_magazine_unlocked");
      const savedLead = localStorage.getItem("producers_today_magazine_lead");
      if (unlocked === "true") {
        setIsSubmitted(true);
        if (savedLead) {
          const parsed = JSON.parse(savedLead);
          setSubmittedName(parsed.firstName || "");
        }
      }
    } catch (e) {
      // Ignore localStorage issues
    }
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      toast.error("Please enter your first and last name.");
      return;
    }

    if (!formData.email.trim() || !formData.email.includes("@")) {
      toast.error("Please enter a valid email address.");
      return;
    }

    if (!formData.phone.trim() || formData.phone.length < 7) {
      toast.error("Please enter a valid phone number.");
      return;
    }

    setIsSubmitting(true);

    const leadPayload = {
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      name: `${formData.firstName.trim()} ${formData.lastName.trim()}`,
      email: formData.email.trim().toLowerCase(),
      phone: formData.phone.trim(),
      source: getLeadSource() || BRAND_NAME,
      brand: BRAND_NAME,
      leadType: "Magazine Reader - Tom Hegna Issue",
      tags: ["Magazine Lead", "Tom Hegna Issue", "Digital Edition", BRAND_NAME],
      magazineUrl: MAGAZINE_URL,
      submittedAt: new Date().toISOString(),
    };

    try {
      if (GHL_WEBHOOK_URL) {
        await axios.post(GHL_WEBHOOK_URL, leadPayload, {
          headers: { "Content-Type": "application/json" },
          timeout: 8000,
        });
      }
    } catch (err) {
      console.warn("GHL Webhook submission error (handled gracefully):", err);
    } finally {
      setIsSubmitting(false);
      setIsSubmitted(true);
      setSubmittedName(formData.firstName.trim());

      try {
        localStorage.setItem("producers_today_magazine_unlocked", "true");
        localStorage.setItem("producers_today_magazine_lead", JSON.stringify(leadPayload));
      } catch (err) {}

      toast.success("Access Granted! Enjoy your copy of Producers Today.");
    }
  };

  const copyMagazineLink = () => {
    navigator.clipboard.writeText(MAGAZINE_URL);
    setCopied(true);
    toast.success("Magazine link copied to clipboard!");
    setTimeout(() => setCopied(false), 2500);
  };

  const handleReset = () => {
    try {
      localStorage.removeItem("producers_today_magazine_unlocked");
      localStorage.removeItem("producers_today_magazine_lead");
    } catch (e) {}
    setIsSubmitted(false);
    setFormData({
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
    });
  };

  return (
    <div className="min-h-screen bg-[#050608] text-white relative flex flex-col">
      {/* Global subtle grid overlay */}
      <div className="global-grid fixed inset-0 pointer-events-none z-0" aria-hidden />

      <Navbar />

      <main className="relative z-15 flex-1 pt-8 pb-16 md:pb-24 px-4 sm:px-6 max-w-6xl mx-auto w-full">
        {/* Back navigation */}
        <div className="mb-6">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-xs md:text-sm font-semibold text-white/50 hover:text-[#f5c451] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Home</span>
          </Link>
        </div>

        {/* Page Header */}
        <div className="text-center max-w-3xl mx-auto mb-10 md:mb-14">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#f5c451]/10 border border-[#f5c451]/30 text-[#f5c451] text-xs font-bold uppercase tracking-widest mb-3">
           Special Digital Release
          </div>
          <h1 className="font-display text-3xl sm:text-4xl md:text-5xl text-white tracking-tight leading-[1.08]">
            THE INCOME ISSUE: <br />
            <span className="text-[#f5c451]">TOM HEGNA</span>
          </h1>
          <p className="text-white/70 text-sm md:text-base mt-3 max-w-2xl mx-auto leading-relaxed">
            Gain complimentary access to the latest edition of <span className="text-white font-semibold">Producers Today</span>, featuring retirement legend Tom Hegna on the math, the science, and the message of annuity income.
          </p>
        </div>

        {/* Main Content Box */}
        <div className="rounded-2xl border border-white/10 bg-gradient-to-b from-white/[0.04] to-black/70 backdrop-blur-xl p-6 sm:p-8 md:p-12 shadow-2xl">
          {!isSubmitted ? (
            <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
              {/* Left Column: Magazine Showcase */}
              <div className="lg:col-span-5 flex flex-col items-center text-center lg:text-left">
                <div className="relative group max-w-[280px] sm:max-w-[320px]">
                  {/* Glowing aura */}
                  <div className="absolute -inset-2 rounded-xl bg-gradient-to-tr from-[#f5c451]/35 via-orange-500/25 to-blue-500/20 blur-xl opacity-75 group-hover:opacity-100 transition duration-500" />
                  
                  <div className="relative rounded-xl overflow-hidden border border-white/20 shadow-[0_20px_50px_rgba(0,0,0,0.9)] bg-black">
                    <img
                      src="/magazine-cover.jpg"
                      alt="Producers Today Magazine - Tom Hegna - The Income Issue"
                      className="w-full h-auto object-cover transform group-hover:scale-[1.02] transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-60 pointer-events-none" />
                    
                    {/* <div className="absolute top-3 left-3 bg-[#f5c451] text-black font-black text-[10px] uppercase tracking-wider px-2.5 py-1 rounded shadow-md">
                      Current Issue
                    </div> */}
                  </div>
                </div>

                {/* Highlights
                <div className="mt-8 space-y-3 w-full max-w-sm text-left">
                  <div className="flex items-center gap-2.5 text-xs text-white/80">
                    <CheckCircle2 className="w-4 h-4 text-[#f5c451] shrink-0" />
                    <span><strong>Tom Hegna</strong> on the math &amp; science of annuity income</span>
                  </div>
                  <div className="flex items-center gap-2.5 text-xs text-white/80">
                    <CheckCircle2 className="w-4 h-4 text-[#f5c451] shrink-0" />
                    <span>Growth &amp; marketing blueprints for top 1% producers</span>
                  </div>
                  <div className="flex items-center gap-2.5 text-xs text-white/80">
                    <CheckCircle2 className="w-4 h-4 text-[#f5c451] shrink-0" />
                    <span>Instant interactive digital flipbook on all devices</span>
                  </div>
                </div> */}
              </div>

              {/* Right Column: Form */}
              <div className="lg:col-span-7 lg:border-l lg:border-white/10 lg:pl-10">
                <div className="mb-6">
                  <div className="flex items-center gap-2 text-xs font-bold text-[#f5c451] uppercase tracking-wider mb-1">
                   Instant Digital Access
                  </div>
                  <h2 className="font-display text-2xl md:text-3xl text-white">
                    Submit To Read Your Free Copy
                  </h2>
                  <p className="text-white/60 text-xs md:text-sm mt-1">
                    Please provide your contact details below to unlock instant, unrestricted reading access.
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="page-first-name" className="text-xs uppercase tracking-wider text-white/70 font-semibold mb-1.5 block">
                        First Name <span className="text-[#f5c451]">*</span>
                      </Label>
                      <Input
                        id="page-first-name"
                        type="text"
                        required
                        value={formData.firstName}
                        onChange={(e) => handleChange("firstName", e.target.value)}
                        placeholder="e.g. John"
                        className="bg-white/5 border-white/15 h-12 text-white placeholder:text-white/35 focus-visible:border-[#f5c451] focus-visible:ring-[#f5c451]/30"
                      />
                    </div>
                    <div>
                      <Label htmlFor="page-last-name" className="text-xs uppercase tracking-wider text-white/70 font-semibold mb-1.5 block">
                        Last Name <span className="text-[#f5c451]">*</span>
                      </Label>
                      <Input
                        id="page-last-name"
                        type="text"
                        required
                        value={formData.lastName}
                        onChange={(e) => handleChange("lastName", e.target.value)}
                        placeholder="e.g. Smith"
                        className="bg-white/5 border-white/15 h-12 text-white placeholder:text-white/35 focus-visible:border-[#f5c451] focus-visible:ring-[#f5c451]/30"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="page-email" className="text-xs uppercase tracking-wider text-white/70 font-semibold mb-1.5 block">
                      Email Address <span className="text-[#f5c451]">*</span>
                    </Label>
                    <Input
                      id="page-email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => handleChange("email", e.target.value)}
                      placeholder="e.g. john@insurancegroup.com"
                      className="bg-white/5 border-white/15 h-12 text-white placeholder:text-white/35 focus-visible:border-[#f5c451] focus-visible:ring-[#f5c451]/30"
                    />
                  </div>

                  <div>
                    <Label htmlFor="page-phone" className="text-xs uppercase tracking-wider text-white/70 font-semibold mb-1.5 block">
                      Phone Number <span className="text-[#f5c451]">*</span>
                    </Label>
                    <Input
                      id="page-phone"
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={(e) => handleChange("phone", e.target.value)}
                      placeholder="e.g. (555) 000-0000"
                      className="bg-white/5 border-white/15 h-12 text-white placeholder:text-white/35 focus-visible:border-[#f5c451] focus-visible:ring-[#f5c451]/30"
                    />
                  </div>

                  <div className="pt-2">
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full btn-gold rounded-md h-14 text-sm md:text-base font-display tracking-widest font-bold uppercase transition-all shadow-lg flex items-center justify-center gap-2"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                          <span>Unlocking Digital Copy...</span>
                        </>
                      ) : (
                        <>
                          <span>GET FREE MAGAZINE ACCESS NOW</span>
                          <ArrowRight className="w-4 h-4" />
                        </>
                      )}
                    </Button>
                  </div>
{/* 
                  <div className="flex items-center justify-center gap-2 pt-2 text-[11px] text-white/40 text-center">
                    <Lock className="w-3.5 h-3.5 text-[#f5c451]" />
                    <span>Instant access granted immediately upon submission. No credit card required.</span>
                  </div> */}
                </form>
              </div>
            </div>
          ) : (
            /* Success State with Magazine Reader */
            <div className="space-y-8 animate-fadeIn">
              {/* Header bar */}
              <div className="bg-[#14b8a6]/10 border border-[#14b8a6]/30 rounded-xl p-5 md:p-6 flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4 text-center md:text-left">
                  <div className="w-12 h-12 rounded-full bg-[#14b8a6]/20 border border-[#14b8a6]/40 flex items-center justify-center shrink-0">
                    <Check className="w-6 h-6 text-[#14b8a6]" strokeWidth={3} />
                  </div>
                  <div>
                    <div className="text-xs font-bold uppercase tracking-wider text-[#14b8a6]">
                      Access Granted ✓
                    </div>
                    <h3 className="font-display text-xl md:text-2xl text-white">
                      Welcome, {submittedName || "Producer"}! Here Is Your Magazine Copy.
                    </h3>
                    <p className="text-white/60 text-xs md:text-sm mt-0.5">
                      Turn pages directly below or expand to fullscreen view.
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3 w-full md:w-auto">
                  <a
                    href={MAGAZINE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 md:flex-none inline-flex items-center justify-center gap-2 bg-[#f5c451] hover:bg-amber-300 text-black font-display font-bold text-xs md:text-sm tracking-wider uppercase px-5 py-3 rounded-md transition-all shadow-md"
                  >
                    <span>Open In New Tab</span>
                    <ExternalLink className="w-4 h-4" />
                  </a>

                  <button
                    onClick={copyMagazineLink}
                    type="button"
                    className="inline-flex items-center justify-center gap-1.5 bg-white/10 hover:bg-white/15 text-white text-xs font-semibold px-4 py-3 rounded-md border border-white/15 transition-all"
                    title="Copy Magazine Link"
                  >
                    {copied ? <Check className="w-4 h-4 text-[#14b8a6]" /> : <Copy className="w-4 h-4" />}
                    <span className="hidden sm:inline">{copied ? "Copied" : "Copy Link"}</span>
                  </button>
                </div>
              </div>

              {/* Embedded Flipbook Reader */}
              <div className="relative rounded-xl overflow-hidden border border-[#f5c451]/30 bg-black shadow-[0_15px_50px_rgba(0,0,0,0.95)]">
                <div className="bg-black/90 border-b border-white/10 px-4 py-2.5 flex items-center justify-between text-xs text-white/70">
                  <div className="flex items-center gap-2 font-semibold">
                    <BookOpen className="w-4 h-4 text-[#f5c451]" />
                    <span className="text-white">Producers Today</span> &bull; Tom Hegna (The Income Issue)
                  </div>
                  <a
                    href={MAGAZINE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#f5c451] hover:text-amber-300 flex items-center gap-1 transition-colors font-medium"
                  >
                    <span>Full Screen</span>
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>

                <div className="relative w-full aspect-[4/3] sm:aspect-[16/10] min-h-[500px] md:min-h-[640px] bg-black">
                  <iframe
                    src={MAGAZINE_URL}
                    title="Producers Today Magazine - Tom Hegna"
                    className="w-full h-full border-0"
                    allow="clipboard-write; fullscreen"
                    allowFullScreen
                  />
                </div>
              </div>

              {/* Footer row with reset option */}
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-white/50 pt-2 border-t border-white/5">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-[#f5c451]" />
                  <span>Your copy is permanently unlocked on this device.</span>
                </div>
                <button
                  type="button"
                  onClick={handleReset}
                  className="text-white/40 hover:text-white/80 underline transition-colors"
                >
                  Enter different contact details
                </button>
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
